import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { getLocale } from '@/services/utils'
import clsx from 'clsx'
import { addDays, differenceInDays, formatDistance } from 'date-fns'

import {
  DbIdent,
  OrgIdent,
  ProjectIdentAdapter,
  randomAppColor,
  randomAppIcon,
  TrashField,
  TrashItemDetail,
  TrashItemKind,
  TrashItemTy,
} from '@makyoapp/makyo-common'
import { App16, BuildingAlt16, Db16, Refresh16, TableFilled24, Team16, Trash16 } from '@makyoapp/makyo-icon/icon'
import {
  AppIcon,
  AvatarCell,
  AvatarSize,
  avatarSizeCls,
  Button,
  ButtonColor,
  Checkbox,
  DeleteModal,
  Loading,
  MoreDropdown,
  ShapeVariant,
  Table,
  Td,
  Tr,
  TrNotFound,
  WrappedPagination,
} from '@makyoapp/makyo-ui'

import { MyTrashProvider, useMyTrashContext } from '@/context/MyTrashContext'
import useAuth from '@/hooks/useAuth'
import { useDateFmt } from '@/hooks/useDateFmt'
import { TRASH_FIELDS } from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'

const TRASH_PER_PAGE = 20

interface TrashItemProps {
  item: TrashItemTy<TrashItemDetail>
  field: TrashField
}

// `getIcon` is a function that determines which icon to display based on the type of the item.
function getIcon(itemType: TrashItemKind) {
  switch (itemType) {
    case TrashItemKind.App:
      return <App16 />
    case TrashItemKind.Db:
      return <Db16 />
    case TrashItemKind.Org:
      return <BuildingAlt16 />
    case TrashItemKind.Team:
      return <Team16 />
    default:
      return null
  }
}

function TrashItem({ item, field }: TrashItemProps) {
  const { userData } = useAuth()
  const dateFmtDefault = useDateFmt()
  switch (field) {
    case 'trashedBy':
      // If item fields is 'trashedBy', render Avatar
      return (
        <Td>
          <AvatarCell size={AvatarSize.XS} {...item.trashedBy} />
        </Td>
      )
    case 'trashedDate':
      // If the field is 'trashedDate', we format the date and return it in a table cell
      return <Td className='trash-date'>{dateFmtDefault(item.trashedDate)}</Td>
    case 'pruneDate':
      // If the field is 'pruneDate', we calculate the prune date and return it in a table cell
      const pruneDate = addDays(item.trashedDate, 14)
      const lang = userData?.lang
      const now = new Date()
      const diff = differenceInDays(pruneDate, now) + 1
      return (
        <Td className={clsx(diff < 3 && 'red', 'trash-date-word')}>
          <div className='whitespace-nowrap'>
            {formatDistance(pruneDate, now, {
              addSuffix: true,
              locale: getLocale(lang),
            })}
          </div>
        </Td>
      )
    case 'type':
      // If the field is 'type', we return a table cell with an icon representing the type
      return (
        <Td className='trash-type'>
          <div title={item.type}>{getIcon(item.type)}</div>
        </Td>
      )
    case 'item':
      // If the field is 'item', we have different renderings depending on the type of item.
      // This could be an App, a Db, a Team, or an Org. Each type has its own rendering logic.
      let inner: React.ReactNode
      switch (item.type) {
        case TrashItemKind.App:
          // If item type is an App, we render an AppIcon with the app's details
          const app = item.item as ProjectIdentAdapter
          inner = (
            <div className='flex-c'>
              <AppIcon
                imgProps={{
                  className: 'p-0',
                }}
                twProps={{
                  className: 'p-1',
                }}
                iconProps={{
                  className: 'p-1',
                }}
                noPad={true}
                shape={ShapeVariant.Square}
                title={app.title}
                icon={app.icon || randomAppIcon(app.title)} // Use the app icon if present, or generate a random one
                defaultSize={false}
                size={AvatarSize.XS}
                className='rounded-md'
                style={{
                  backgroundColor: randomAppColor(app.title), // Background color is a random color based on app title
                }}
              />
              <div className='w-full'>{app.title}</div>
            </div>
          )
          break
        case TrashItemKind.Db:
          // If item type is a Db, we render a TableFilled24 icon and the database name
          const db = item.item as DbIdent
          inner = (
            <div className='flex-c'>
              <div className={clsx('flex-cc', avatarSizeCls(AvatarSize.XS))}>
                <TableFilled24 className='fill-primary-500' />
              </div>
              <span>{db.name}</span>
            </div>
          )
          break
        case TrashItemKind.Team:
        case TrashItemKind.Org:
        default:
          // If item type is a Team or Org or any other type, we render an AvatarCell with the org's details
          const org = item.item as OrgIdent
          inner = (
            <AvatarCell
              {...org}
              size={AvatarSize.XS}
              shape={item.type === TrashItemKind.Team ? ShapeVariant.Rounded : undefined}
            />
          )
          break
      }
      return <Td className='trash-item'>{inner}</Td>
    default:
      return <Td>{item[field]?.toString()}</Td>
  }
}

/**
 * trash table view
 */
function TrashTable() {
  const { t } = useTranslation()
  const { isLoading, trashes, checks, setChecks, handleRestore, handleDel } = useMyTrashContext()

  const [delPermanent, setDelPermanent] = useState<TrashItemTy<TrashItemDetail> | null>(null)
  const [isDelLoading, setIsDelLoading] = useState(false)

  const handleCheckboxClick = useMemo(
    () => (isChecked: TrashItemTy<TrashItemDetail> | undefined, trash: TrashItemTy<TrashItemDetail>) => {
      if (isChecked) {
        // If the item is already checked, remove it from the `checks` state.
        setChecks(checks.filter((c) => c.id !== trash.id))
      } else {
        // If the item is not checked, add it to the `checks` state.
        setChecks([...checks, trash])
      }
    },
    // The function returned by `useMemo` is recalculated when either `setChecks` or `checks` changes.
    [setChecks, checks]
  )

  return (
    <>
      <Table className='border bdr'>
        <thead>
          <Tr>
            {/* This first table header creates a checkbox that, when clicked, either selects all items or deselects all items. */}
            <th className='chk'>
              <Checkbox
                checked={trashes.length > 0 && checks.length === trashes.length}
                onClick={() => {
                  // If not all items are currently selected, clicking the checkbox selects all items.
                  // If all items are currently selected, clicking the checkbox deselects all items.
                  if (checks.length !== trashes.length) {
                    setChecks([...trashes])
                  } else {
                    setChecks([])
                  }
                }}
              />
            </th>
            {/* Then, for each field specified in `TRASH_FIELDS`, a table header is created. */}
            {TRASH_FIELDS.map((f) => {
              return (
                <th key={f} className={clsx('whitespace-nowrap', f !== 'item' && 'w-[20%]')}>
                  <div
                    className={clsx(
                      f === 'item' && 'trash-item',
                      f === 'trashedDate' && 'trash-date',
                      f === 'pruneDate' && 'trash-date-word',
                      f === 'type' && 'trash-type'
                    )}
                  >
                    {/* The text of the table header is the localized string for the field. */}
                    {t(f)}
                  </div>
                </th>
              )
            })}
            {/* The last table header is for the actions column, which doesn't need a header text. */}
            <th className='act trash-act' />
          </Tr>
        </thead>
        <tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={TRASH_FIELDS.length + 2}>
                <Loading fullHeight={false} />
              </Td>
            </Tr>
          ) : trashes.length > 0 ? (
            trashes.map((trash) => {
              // Check if the current item is already checked.
              const isCurrentlyChecked = checks.find((c) => c.id === trash.id)
              return (
                // For each item in the trash, a row is created in the table.
                <Tr key={`tr-${trash.id}`}>
                  {/* The first column is a checkbox that marks the item for selection. */}
                  <Td className='chk'>
                    <Checkbox
                      id={`${trash.id}-chk`}
                      checked={isCurrentlyChecked !== undefined}
                      onClick={() => handleCheckboxClick(isCurrentlyChecked, trash)}
                    />
                  </Td>
                  {/* Then, for each field specified in `TRASH_FIELDS`, a `TrashItem` component is rendered. */}
                  {TRASH_FIELDS.map((f, index) => {
                    return <TrashItem key={`${f}-${index}`} item={trash} field={f} />
                  })}
                  {/* The last column is a dropdown menu that provides options to restore the item or delete it permanently. */}
                  <td className='act trash-act'>
                    <MoreDropdown menuBtnClassName='mk-trash-menu' id={`${trash.id}-mdd`}>
                      <MoreDropdown.MenuItem id={`${trash.id}-mdd-restore`} onClick={() => handleRestore([trash])}>
                        <Refresh16 />
                        <div>
                          <Trans>Restore</Trans>
                        </div>
                      </MoreDropdown.MenuItem>
                      <MoreDropdown.MenuItem
                        id={`${trash.id}-mdd-trash`}
                        className='red'
                        onClick={() => {
                          setDelPermanent(trash)
                        }}
                      >
                        <Trash16 />
                        <div>
                          <Trans>Delete Permanently</Trans>
                        </div>
                      </MoreDropdown.MenuItem>
                    </MoreDropdown>
                  </td>
                </Tr>
              )
            })
          ) : (
            <TrNotFound colSpan={TRASH_FIELDS.length + 2} />
          )}
        </tbody>
      </Table>
      {delPermanent &&
        (() => {
          // `resetDelPermanent` is a function that sets `delPermanent` to null,
          // effectively "resetting" the deletion process.
          const resetDelPermanent = () => setDelPermanent(null)

          // `handleDelete` is a function that starts the deletion process.
          // It calls `handleDel` with the item to be deleted and options to
          // control the loading state and reset the deletion process after it's done.
          const handleDelete = () => {
            handleDel([delPermanent], {
              bulk: false,
              onStart: () => setIsDelLoading(true),
              onFinally: () => {
                setIsDelLoading(false)
                resetDelPermanent()
                toast.error('Item has been deleted permanently')
              },
            })
          }

          // The return statement renders a `DeleteModal` when `delPermanent` is truthy.
          // The modal displays a warning message and offers options to either
          // continue with the deletion or cancel it.
          return (
            <DeleteModal
              show={true}
              isLoading={isDelLoading}
              onHide={resetDelPermanent}
              onSubmit={handleDelete}
              onCancel={resetDelPermanent}
              withCancel
              cancelLabel={t('Cancel')}
              title={t('Permanent Deletion')}
              continueLabel={t('Delete Permanently')}
            >
              <div className='modal-md'>
                <div className='text-sm mb-2'>{t('Are you sure you want to delete this item forever?')}</div>
                <div className='text-light'>{t('This action cannot be undone.')}</div>
              </div>
            </DeleteModal>
          )
        })()}
    </>
  )
}

/**
 * user trash layout
 * depends on `MyTrashContext`
 */
function UserTrashLayout(props: WrapperProps) {
  const { t } = useTranslation()

  const { allTrashes, setTrashes, page, setPage, checks, setChecks, handleRestore, handleDel } = useMyTrashContext()

  // request set trash based on
  // - page
  useEffect(() => {
    const offset = (page - 1) * TRASH_PER_PAGE
    setTrashes(allTrashes.items.slice(offset, offset + TRASH_PER_PAGE))
  }, [page, allTrashes, setTrashes])

  const [delPerm, setDelPerm] = useState(false)
  const [delPermLoading, setDelPermLoading] = useState(false)

  const handlePermDelete = () => {
    handleDel(checks, {
      bulk: true,
      onStart: () => {
        setDelPermLoading(true)
      },
      onFinally: () => {
        setChecks([])
        setDelPerm(false)
        setDelPermLoading(false)
        toast.error('Items have been deleted permanently')
      },
    })
  }

  const handlePageChange = (_f: number, _t: number, newPage: number) => {
    if (page !== newPage) {
      setChecks([])
      setPage(newPage)
    }
  }

  return (
    <Layout
      title={props.title}
      actions={
        <>
          <Button
            id='trash-restore-selected'
            disabled={checks.length < 1}
            icon={<Refresh16 />}
            onClick={() => handleRestore(checks)}
          >
            {t('Restore Selected')}
          </Button>
          <Button
            id='trash-delete-selected'
            colorStyle={ButtonColor.Danger}
            disabled={checks.length < 1}
            icon={<Trash16 />}
            onClick={() => setDelPerm(true)}
          >
            {t('Delete Selected Permanently')}
          </Button>
        </>
      }
    >
      <TrashTable />
      <WrappedPagination
        id='trash-pg'
        totalCount={allTrashes.totalCount}
        page={page}
        perPage={TRASH_PER_PAGE}
        onChange={handlePageChange}
      />
      {delPerm && (
        <DeleteModal
          show={true}
          onHide={() => setDelPerm(false)}
          withCancel
          isLoading={delPermLoading}
          onCancel={() => setDelPerm(false)}
          onSubmit={handlePermDelete}
          continueLabel={t('Delete Permanently')}
          title={t('Permanent Deletion')}
        >
          <div className='modal-md'>
            <div className='text-sm mb-2'>{t('Are you sure you want to delete these items forever?')}</div>
            <div className='text-light'>{t('This action cannot be undone.')}</div>
          </div>
        </DeleteModal>
      )}
    </Layout>
  )
}

/**
 * User Trash page
 */
export default function MyTrash(props: WrapperProps) {
  return (
    <MyTrashProvider>
      <UserTrashLayout {...props} />
    </MyTrashProvider>
  )
}
